import React, { useState, useEffect } from "react";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import ImageInput from "../../common/ImageInput";
import FilePreview from "../../FilePreview/FilePreview";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as eventCategoryService from "./service";
import useTableSearch from "../../../hooks/useTableSearch";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../../common/adminTable";

const isValidImageUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (
    trimmed.endsWith("/event-category-master") ||
    trimmed.endsWith("/event-category-master/")
  ) {
    return false;
  }
  return true;
};

const EventCategoryThumb = ({ image }) => {
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    setHasError(false);
  }, [image]);

  const hasImage = isValidImageUrl(image) && !hasError;

  if (!hasImage) {
    return (
      <div
        className="flex h-7 w-14 items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50 text-[10px] font-medium text-gray-400"
        title="No image"
      >
        No image
      </div>
    );
  }

  return (
    <img
      src={image}
      alt="Event category preview"
      className="h-7 w-7 object-cover rounded-md"
      onError={() => setHasError(true)}
    />
  );
};

const EventCategory = () => {
  const queryClient = useQueryClient();

  // State management
  const [selectedEventCategory, setSelectedEventCategory] = useState(null);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState(1);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [errors, setErrors] = useState({
    title: "",
    thumbImage: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  // Image states
  const [thumbImage, setThumbImage] = useState(null);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Queries
  const { data: eventCategoryData } = useQuery({
    queryKey: ["eventCategories", currentPage, activeSearch],
    queryFn: () =>
      eventCategoryService.list({ page: currentPage, search: activeSearch || undefined }),
    keepPreviousData: true,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: eventCategoryService.create,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(
        success.response?.data?.message || "Event category added successfully!"
      );
      setAddDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["eventCategories"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(
        error.response?.data?.message || "Failed to add event category"
      );
      setShowError(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: eventCategoryService.update,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(
        success.response?.data?.message ||
          "Event category updated successfully!"
      );
      setEditDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["eventCategories"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(
        error.response?.data?.message || "Failed to update event category"
      );
      setShowError(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: eventCategoryService.deleteEventCategory,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(
        success.response?.data?.message ||
          "Event category deleted successfully!"
      );
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries(["eventCategories"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(
        error.response?.data?.message || "Failed to delete event category"
      );
      setShowError(true);
    },
  });

  const getByIdMutation = useMutation({
    mutationFn: eventCategoryService.getById,
    onSuccess: (data) => {
      setIsLoading(false);
      setSelectedEventCategory(data.data);
      setTitle(data.data.name || data.data.title);
      setStatus(data.data.status);
      setThumbImage(null);
      setEditDialogOpen(true);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.message || "Failed to fetch event category details"
      );
    },
  });

  // Helper functions
  const resetForm = () => {
    setTitle("");
    setStatus(1);
    setThumbImage(null);
    setErrors({ title: "", thumbImage: "" });
  };

  const validateForm = () => {
    let tempErrors = {
      title: "",
      thumbImage: "",
    };
    let isValid = true;

    if (!title.trim()) {
      tempErrors.title = "Title is required";
      isValid = false;
    }

    // For new categories, thumbImage is required
    // For updates, thumbImage is optional (can keep existing)
    if (!selectedEventCategory && !thumbImage) {
      tempErrors.thumbImage = "Thumb image is required for new categories";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (errorMsg) => {
    setErrorMessage(errorMsg);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 3000);
  };

  // Action handlers
  const handleAddClick = () => {
    resetForm();
    setStatus(1);
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (eventCategory) => {
    setSelectedEventCategory(eventCategory);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (eventCategoryId) => {
    setIsLoading(true);
    getByIdMutation.mutate(eventCategoryId);
  };

  const handleSaveEventCategory = () => {
    setIsLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", title);
      formData.append("status", Number(status));

      if (thumbImage) {
        formData.append("image", thumbImage);
      }

      createMutation.mutate(formData);
    } else {
      setIsLoading(false);
    }
  };

  const handleUpdateEventCategory = () => {
    setIsLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", title);
      formData.append("status", Number(status));

      // Only append thumb_image if a new one is selected
      if (thumbImage) {
        formData.append("image", thumbImage);
      }

      updateMutation.mutate({
        id: selectedEventCategory.id,
        eventCategoryData: formData,
      });
    } else {
      setIsLoading(false);
    }
  };

  const handleDeleteEventCategory = () => {
    setIsLoading(true);
    if (selectedEventCategory) {
      deleteMutation.mutate(selectedEventCategory.id);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const formatDate = formatListingDateTime;

  const pagination = {
    currentPage: eventCategoryData?.data?.current_page || 1,
    lastPage: eventCategoryData?.data?.last_page || 1,
    perPage: eventCategoryData?.data?.per_page || 10,
    total: eventCategoryData?.data?.total || 0,
    from: eventCategoryData?.data?.from || 0,
    to: eventCategoryData?.data?.to || 0,
  };

  const eventCategories = eventCategoryData?.data?.data || [];

  return (
    <AdminPageLayout>
      {/* Error Toster */}
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.eventCategory.title}
        subtitle={ADMIN_PAGE_META.eventCategory.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              placeholder="Search event categories..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <button
              onClick={handleAddClick}
              className="bg-primary text-white px-4 py-1 rounded-lg border border-primary shadow-md hover:bg-primary/90 transition-all"
            >
              Add Event Category
            </button>
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                showInfo={true}
              />
            </AdminTableFooter>
          }
        >
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Preview</AdminTableTh>
          <AdminTableTh>Title</AdminTableTh>
          <AdminTableTh>Created Date</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {eventCategories.length > 0 ? (
            eventCategories.map((eventCategory, index) => (
              <AdminTableRow key={eventCategory.id}>
                <AdminTableCell>
                  {(currentPage - 1) * pagination.perPage + index + 1}
                </AdminTableCell>
                <AdminTableCell>
                  <EventCategoryThumb image={eventCategory.image} />
                </AdminTableCell>
                <AdminTableCell>
                  {eventCategory.name || eventCategory.title}
                </AdminTableCell>
                <AdminTableCell>
                  {formatDate(eventCategory.created_at)}
                </AdminTableCell>
                <AdminTableCell>
                  <StatusBadge variant={getStatusVariant(eventCategory.status)}>
                    {eventCategory.status === 1 ? "Active" : "Inactive"}
                  </StatusBadge>
                </AdminTableCell>
                <TableActionsCell>
                  <EditActionButton
                    showLabel={false}
                    onClick={() => handleEditClick(eventCategory.id)}
                  />
                  <DeleteActionButton
                    showLabel={false}
                    onClick={() => handleDeleteClick(eventCategory)}
                  />
                </TableActionsCell>
              </AdminTableRow>
            ))
          ) : (
            <TableEmptyRow
              colSpan={6}
              emptyMessage="No event categories found"
              searchQuery={activeSearch}
              onClearSearch={clearSearch}
            />
          )}
        </AdminTableBody>
      </AdminTable>
      </AdminPageCard>

      {/* Add Event Category Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setErrors({ title: "", thumbImage: "" });
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-bs-add"></i>
            Create Event Category
          </div>
        }
        primaryButtonText="Add Event Category"
        primaryButtonAction={handleSaveEventCategory}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          setErrors({ title: "", thumbImage: "" });
        }}
        isLoading={isLoading}
      >
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={`text-sm w-full px-3 py-1 border ${
              errors.title ? "border-red-500" : "border-gray-300"
            } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
            placeholder="Enter event category title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setErrors({ ...errors, title: "" });
            }}
          />
          {errors.title && (
            <p className="text-red-500 text-xs mt-1">{errors.title}</p>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
            value={status}
            onChange={(e) => setStatus(Number(e.target.value))}
          >
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </div>

        {/* Image Input Fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Thumb Image <span className="text-red-500">*</span>
          </label>
          <ImageInput
            onChange={(file) => {
              setThumbImage(file);
              setErrors({ ...errors, thumbImage: "" });
            }}
            label="Upload Thumb Image"
            required={true}
            accept="image/*"
          />
          {errors.thumbImage && (
            <p className="text-red-500 text-xs mt-1">{errors.thumbImage}</p>
          )}
          {thumbImage && (
            <div className="mt-2">
              <FilePreview
                file={thumbImage}
                label="Thumb Image Preview"
                onDelete={() => setThumbImage(null)}
                isImage={true}
                showDelete={true}
              />
            </div>
          )}
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Edit Event Category
          </div>
        }
        primaryButtonText="Update"
        primaryButtonAction={handleUpdateEventCategory}
        closeButtonText="Cancel"
        closeButtonAction={() => setEditDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={`text-sm w-full px-3 py-1 border ${
              errors.title ? "border-red-500" : "border-gray-300"
            } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
            placeholder="Enter event category title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setErrors({ ...errors, title: "" });
            }}
          />
          {errors.title && (
            <p className="text-red-500 text-xs mt-1">{errors.title}</p>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
            value={status}
            onChange={(e) => setStatus(Number(e.target.value))}
          >
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </div>

        {/* Image Input Fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Thumb Image
          </label>
          <ImageInput
            onChange={(file) => {
              setThumbImage(file);
              setErrors({ ...errors, thumbImage: "" });
            }}
            label="Upload Thumb Image"
            accept="image/*"
          />
          {errors.thumbImage && (
            <p className="text-red-500 text-xs mt-1">{errors.thumbImage}</p>
          )}
          {thumbImage ? (
            <div className="mt-2">
              <FilePreview
                file={thumbImage}
                label="Thumb Image Preview"
                onDelete={() => setThumbImage(null)}
                isImage={true}
                showDelete={true}
              />
            </div>
          ) : selectedEventCategory && isValidImageUrl(selectedEventCategory.image) ? (
            <div className="mt-2">
              <FilePreview
                fileUrl={selectedEventCategory.image}
                label="Current Thumb Image"
                isImage={true}
                showDelete={false}
              />
            </div>
          ) : (
            <div
              className="mt-2 flex h-7 w-14 items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50 text-[10px] font-medium text-gray-400"
              title="No image"
            >
              No image
            </div>
          )}
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-[16px]"></i>
            Delete Event Category
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteEventCategory}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this event category? This action
            cannot be undone.
          </p>
        </div>
      </Dialog>
    </AdminPageLayout>
  );
};

export default EventCategory;
